import React from 'react';

const DateSelector = ({ 
  startDate, 
  endDate, 
  onStartDateChange, 
  onEndDateChange, 
  label = "Tournament Dates",
  required = false,
  className = ""
}) => {
  
  // Format date for display (e.g., "July 9, 2025")
  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      // Parse the date string manually to avoid timezone issues
      const [year, month, day] = dateString.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-based in Date constructor
      
      if (isNaN(date.getTime())) return dateString; // Return original if invalid
      
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      return dateString; // Return original if error
    }
  };

  // Format date range for display
  const formatDateRange = (start, end) => {
    if (!start && !end) return '';
    if (!start) return formatDisplayDate(end);
    if (!end) return formatDisplayDate(start);
    
    const startFormatted = formatDisplayDate(start);
    const endFormatted = formatDisplayDate(end);
    
    if (start === end) {
      return startFormatted;
    }
    
    return `${startFormatted} - ${endFormatted}`;
  };

  // Get today's date in YYYY-MM-DD format for min attribute
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  return (
    <div className={`form-group ${className}`}>
      <label className={`form-label ${required ? 'required' : ''}`}>
        {label}
      </label>
      
      {/* Date Input Fields */}
      <div className="form-group-inline">
        <div className="form-group">
          <label className="form-label">Start Date</label>
          <input
            type="date"
            value={startDate || ''}
            onChange={(e) => onStartDateChange(e.target.value)}
            min={getTodayDate()}
            className="form-input"
          />
        </div>
        
        <div className="form-group">
          <label className="form-label">End Date</label>
          <input
            type="date"
            value={endDate || ''}
            onChange={(e) => onEndDateChange(e.target.value)}
            min={startDate || getTodayDate()}
            className="form-input"
          />
        </div>
      </div>
      
      {/* Display Preview */}
      {(startDate || endDate) && (
        <div className="mt-2 p-3 bg-powder-blue-light border border-powder-blue rounded-md">
          <div className="text-sm font-medium text-gray-700 mb-1">Preview:</div>
          <div className="text-lg font-semibold text-gray-900">
            {formatDateRange(startDate, endDate)}
          </div>
        </div>
      )}
      
      {/* Helper Text */}
      <div className="text-sm text-gray-600 mt-1">
        Select the tournament dates. For single-day tournaments, use the same date for both start and end.
      </div>
    </div>
  );
};

// Utility functions for date formatting (export for use in other components)
export const formatTournamentDate = (startDate, endDate) => {
  // Treat 'null', null, and '' as unset
  const isUnset = (val) => val === null || val === undefined || val === '' || val === 'null';
  if (isUnset(startDate) && isUnset(endDate)) return '';
  if (isUnset(startDate)) return formatDisplayDate(endDate);
  if (isUnset(endDate)) return formatDisplayDate(startDate);

  const startFormatted = formatDisplayDate(startDate);
  const endFormatted = formatDisplayDate(endDate);

  if (startDate === endDate) {
    return startFormatted;
  }

  return `${startFormatted} - ${endFormatted}`;
};

export const formatDisplayDate = (dateString) => {
  // Treat 'null', null, and '' as unset
  if (!dateString || dateString === 'null') return '';
  try {
    // Parse the date string manually to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-based in Date constructor
    if (isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    return '';
  }
};

export default DateSelector; 